import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  createStore,
  FlipdishConfig,
  StoreCreationInput,
  StoreCreationResult,
  StepCallback,
  Currency,
  StoreAddress,
  MannaStoreInput,
  PartialCreationState,
} from './flipdishApi';
import { addPartner, uploadImage } from '../../actions/partnerActions';
import { merchantCategories } from '../../constants/merchantConstants';
import { orderfulApi } from '../../services/api-instances';
import { Cell, LandingZone, PointOfSale } from '@m4a3/api-client';
import { logToDatadog } from '../../services/datadog';

function sanitizeError(err: any) {
  return {
    message: err?.message,
    status: err?.response?.status,
    responseData: err?.response?.data,
    url: err?.config?.url,
  };
}

export enum StepStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

interface CreationStep {
  label: string;
  status: StepStatus;
  detail?: string;
}

const DEFAULT_CONFIG: FlipdishConfig = {
  appId: 'br2641',
  orgId: 'org3033',
  templateSalesChannelId: 'sc22427',
  orderfulApi,
};

const EMPTY_ADDRESS: StoreAddress = {
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  city: '',
  postcode: '',
  countryCode: 'IE',
  state: '',
};

const EMPTY_INPUT: StoreCreationInput = {
  name: '',
  currency: 'EUR',
  phone: '',
  email: 'gavin.hayes@flipdish.com',
  address: { ...EMPTY_ADDRESS },
  latitude: '',
  longitude: '',
};

const EMPTY_MANNA_INPUT: MannaStoreInput = {
  cellId: '',
  position: '',
  leadTime: '',
  travelTime: '',
  category: '',
  landingZoneId: '',
  orderHrLimit: '0',
  preorderAvail: false,
  autoAcceptEnabled: false,
  autoPreparingEnabled: false,
  autoPreparedEnabled: false,
};

const FD_STEPS: CreationStep[] = [
  { label: 'Authenticate with Flipdish', status: StepStatus.PENDING },
  { label: 'Create Property (v3.0 API)', status: StepStatus.PENDING },
  { label: 'Set property opening hours', status: StepStatus.PENDING },
  { label: 'Clone golden template sales channel', status: StepStatus.PENDING },
  { label: 'Publish sales channel', status: StepStatus.PENDING },
  { label: 'Wait for store provisioning', status: StepStatus.PENDING },
  { label: 'Create Sales Channel Group & assign store', status: StepStatus.PENDING },
  { label: 'Create menu with delivery fee', status: StepStatus.PENDING },
  { label: 'Assign menu to store', status: StepStatus.PENDING },
  { label: 'Update store address', status: StepStatus.PENDING },
  { label: 'Verify store under app', status: StepStatus.PENDING },
];

const MANNA_STEPS: CreationStep[] = [
  { label: 'Create partner', status: StepStatus.PENDING },
  { label: 'Upload logos', status: StepStatus.PENDING },
  { label: 'Create Manna store', status: StepStatus.PENDING },
];

const ALL_STEPS = [...FD_STEPS, ...MANNA_STEPS];

const StoreCreation = () => {
  const [config] = useState<FlipdishConfig>(DEFAULT_CONFIG);
  const [cells, setCells] = useState<Cell[]>([]);
  const [landingZones, setLandingZones] = useState<LandingZone[]>([]);
  const [storeInput, setStoreInput] = useState<StoreCreationInput>(EMPTY_INPUT);
  const [mannaInput, setMannaInput] = useState<MannaStoreInput>(EMPTY_MANNA_INPUT);

  useEffect(() => {
    orderfulApi.getAllCells().then(setCells).catch(() => {});
  }, []);

  useEffect(() => {
    if (mannaInput.cellId) {
      setMannaInput(prev => ({ ...prev, landingZoneId: '' }));
      orderfulApi.getAllLandingZonesInCell(parseInt(mannaInput.cellId), { page: 0, size: 1000 })
        .then(res => setLandingZones(res.entities || []))
        .catch(() => setLandingZones([]));
    }
  }, [mannaInput.cellId]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [largeLogoFile, setLargeLogoFile] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [steps, setSteps] = useState<CreationStep[]>(ALL_STEPS);
  const [result, setResult] = useState<StoreCreationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [flipdishOnly, setFlipdishOnly] = useState(false);
  const [showFlipdishOnlyModal, setShowFlipdishOnlyModal] = useState(false);
  const [skipMenu, setSkipMenu] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [partialState, setPartialState] = useState<PartialCreationState | null>(null);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugFailAtStep, setDebugFailAtStep] = useState<number | string | undefined>(undefined);

  const isConfigValid =
    config.appId.trim().length > 0 &&
    config.templateSalesChannelId.trim().length > 0;

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(storeInput.email);

  const latRaw = storeInput.latitude.trim();
  const lngRaw = storeInput.longitude.trim();
  const isLatValid = latRaw === '' || Number.isFinite(Number.parseFloat(latRaw));
  const isLngValid = lngRaw === '' || Number.isFinite(Number.parseFloat(lngRaw));
  const hasPartialCoords = (latRaw !== '') !== (lngRaw !== '');

  const cc = storeInput.address.countryCode;
  const isFlipdishFieldsValid =
    isConfigValid &&
    storeInput.name.trim().length > 0 &&
    isValidEmail &&
    isLatValid && isLngValid && !hasPartialCoords &&
    storeInput.address.addressLine1.trim().length > 0 &&
    storeInput.address.addressLine2.trim().length > 0 &&
    (cc !== 'GB' || (storeInput.address.addressLine3 ?? '').trim().length > 0) &&
    (cc !== 'US' || (storeInput.address.state ?? '').trim().length === 2) &&
    storeInput.address.city.trim().length > 0 &&
    storeInput.address.postcode.trim().length > 0 &&
    storeInput.address.countryCode.trim().length > 0;

  const isMannaFieldsValid =
    mannaInput.cellId.trim().length > 0 &&
    mannaInput.position.trim().length > 0 &&
    mannaInput.leadTime.trim().length > 0 &&
    mannaInput.travelTime.trim().length > 0 &&
    mannaInput.category.trim().length > 0 &&
    mannaInput.landingZoneId.trim().length > 0 &&
    logoFile !== null &&
    largeLogoFile !== null;

  const isFormValid = flipdishOnly ? isFlipdishFieldsValid : (isFlipdishFieldsValid && isMannaFieldsValid);
  const hasFailedPartial = partialState != null && partialState.failedAtStep != null;

  const updateStoreInput = (field: keyof StoreCreationInput, value: string) => {
    setStoreInput(prev => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field: keyof StoreAddress, value: string) => {
    setStoreInput(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const resetSteps = () =>
    ALL_STEPS.map(s => ({ ...s, status: StepStatus.PENDING, detail: undefined }));

  const resetForm = () => {
    setStoreInput({ ...EMPTY_INPUT, address: { ...EMPTY_ADDRESS } });
    setMannaInput({ ...EMPTY_MANNA_INPUT });
    setLogoFile(null);
    setLargeLogoFile(null);
    setFileInputKey(k => k + 1);
    setSkipMenu(false);
    setResult(null);
    setError(null);
    setSteps(resetSteps());
    setPartialState(null);
    setShowRetryModal(false);
    setDebugFailAtStep(undefined);
    setShowDebugPanel(false);
  };

  const updateMannaInput = (field: keyof MannaStoreInput, value: string) => {
    setMannaInput(prev => ({ ...prev, [field]: value }));
  };

  const runCreation = async (resume?: PartialCreationState) => {
    if (isCreating) return;

    setIsCreating(true);
    setError(null);
    setResult(null);
    setShowRetryModal(false);

    if (!resume) {
      setPartialState(null);
      setSteps(resetSteps());
    } else {
      setSteps(prev =>
        prev.map(s =>
          s.status === StepStatus.FAILED ? { ...s, status: StepStatus.PENDING, detail: undefined } : s
        )
      );
    }

    const onStep: StepCallback = (stepIndex, status, detail) => {
      setSteps(prev =>
        prev.map((s, idx) => {
          if (idx !== stepIndex) return s;
          if (status === 'start') return { ...s, status: StepStatus.IN_PROGRESS, detail: detail || undefined };
          if (status === 'done') return { ...s, status: StepStatus.SUCCESS, detail };
          return { ...s, status: StepStatus.FAILED, detail };
        })
      );
    };

    const onPartialState = (state: PartialCreationState) => {
      setPartialState(state);
    };

    try {
      const creationResult = await createStore(config, { ...storeInput, skipMenu }, onStep, onPartialState, resume, debugFailAtStep);

      if (flipdishOnly) {
        logToDatadog('store_creation_success_flipdish_only', {
          storeName: storeInput.name,
          storeId: creationResult.storeId,
          salesChannelId: creationResult.salesChannelId,
          propertyId: creationResult.propertyId,
          storeGroupId: creationResult.storeGroupId,
          menuId: creationResult.menuId,
        });
        setResult({ ...creationResult, partnerName: storeInput.name });
        setPartialState(null);
        return;
      }

      const ms = FD_STEPS.length; // index of first Manna step (Create Partner)
      const mannaDebugFail = (key: string) => {
        if (debugFailAtStep !== undefined && String(debugFailAtStep) === key) {
          throw new Error(`[DEBUG] Forced failure at Manna step ${key}`);
        }
      };

      let partnerId: number | null = resume?.partnerId ?? null;
      if (!partnerId) {
        onStep(ms, 'start');
        try {
          mannaDebugFail('m11');
          const partner = await addPartner({ partnerName: storeInput.name, active: true } as any);
          partnerId = partner?.id ?? null;
          if (partnerId == null) throw new Error('Partner created but no ID returned');
          setPartialState(prev => ({ ...(prev ?? {}), partnerId }));
          onStep(ms, 'done', `Partner "${storeInput.name}" created (ID: ${partnerId})`);
        } catch (partnerErr: any) {
          logToDatadog('store_creation_error_create_partner', { error: sanitizeError(partnerErr), storeName: storeInput.name }, 'error');
          onStep(ms, 'error', partnerErr.message || 'Failed to create partner');
          setPartialState(prev => ({ ...(prev ?? {}), failedAtStep: ms }));
          throw partnerErr;
        }
      } else {
        onStep(ms, 'done', `Partner ID: ${partnerId} (resumed)`);
      }

      let logoUrl = resume?.logoUrl ?? '';
      let largeLogoUrl = resume?.largeLogoUrl ?? '';
      if (!logoUrl || !largeLogoUrl) {
        onStep(ms + 1, 'start');
        try {
          mannaDebugFail('m12');
          if (logoFile && !logoUrl) {
            const logoFormData = new FormData();
            logoFormData.append('image', logoFile, logoFile.name);
            const logoRes = await uploadImage(logoFormData, logoFile.name);
            logoUrl = logoRes.fileUrl;
            setPartialState(prev => ({ ...(prev ?? {}), logoUrl }));
          }
          if (largeLogoFile && !largeLogoUrl) {
            const largeFormData = new FormData();
            largeFormData.append('image', largeLogoFile, largeLogoFile.name);
            const largeRes = await uploadImage(largeFormData, largeLogoFile.name);
            largeLogoUrl = largeRes.fileUrl;
            setPartialState(prev => ({ ...(prev ?? {}), largeLogoUrl }));
          }
          onStep(ms + 1, 'done', 'Logos uploaded');
        } catch (logoErr: any) {
          logToDatadog('store_creation_error_upload_logos', { error: sanitizeError(logoErr), storeName: storeInput.name }, 'error');
          onStep(ms + 1, 'error', logoErr.message || 'Failed to upload logos');
          setPartialState(prev => ({ ...(prev ?? {}), failedAtStep: ms + 1 }));
          throw logoErr;
        }
      } else {
        onStep(ms + 1, 'done', 'Logos (resumed)');
      }

      let posId: number | null = resume?.posId ?? null;
      if (!posId) {
        onStep(ms + 2, 'start');
        try {
          mannaDebugFail('m13');
          const posPayload = {
            posId: '',
            posName: storeInput.name,
            partnerId,
            cellId: parseInt(mannaInput.cellId),
            displayStore: false,
            sortOrder: parseInt(mannaInput.position),
            logo: logoUrl,
            largeLogo: largeLogoUrl,
            leadTime: parseInt(mannaInput.leadTime),
            fdAppId: creationResult.storeId,
            fdMenuId: skipMenu ? null : creationResult.menuId,
            fdStoreId: creationResult.storeId,
            category: mannaInput.category,
            open: false,
            openPreorder: false,
            preorderAvail: mannaInput.preorderAvail,
            orderHrLimit: parseInt(mannaInput.orderHrLimit) || 0,
            landingZoneId: mannaInput.landingZoneId || null,
            travelTime: parseInt(mannaInput.travelTime),
            deliveryFee: 4.99,
            autoAcceptEnabled: mannaInput.autoAcceptEnabled,
            autoPreparingEnabled: mannaInput.autoPreparingEnabled,
            autoPreparedEnabled: mannaInput.autoPreparedEnabled,
          } as unknown as PointOfSale;

          const createdPos = await orderfulApi.addPointOfSale(posPayload);
          posId = createdPos?.id ?? null;
          setPartialState(prev => ({ ...(prev ?? {}), posId: posId ?? undefined }));
          onStep(ms + 2, 'done', `Manna store created (POS ID: ${posId})`);
        } catch (posErr: any) {
          logToDatadog('store_creation_error_create_manna_store', { error: sanitizeError(posErr), storeName: storeInput.name, flipdishResult: creationResult }, 'error');
          onStep(ms + 2, 'error', posErr.message || 'Failed to create Manna store');
          setPartialState(prev => ({ ...(prev ?? {}), failedAtStep: ms + 2 }));
          throw posErr;
        }
      } else {
        onStep(ms + 2, 'done', `Manna store POS ID: ${posId} (resumed)`);
      }

      orderfulApi.reloadCache();

      const finalResult = { ...creationResult, partnerName: storeInput.name };
      logToDatadog('store_creation_success', {
        storeName: storeInput.name,
        storeId: finalResult.storeId,
        salesChannelId: finalResult.salesChannelId,
        propertyId: finalResult.propertyId,
        storeGroupId: finalResult.storeGroupId,
        menuId: finalResult.menuId,
        partnerId,
        posId,
        cellId: mannaInput.cellId,
        category: mannaInput.category,
      });
      setResult(finalResult);
      setPartialState(null);
    } catch (err: any) {
      const respData = err.response?.data;
      const message =
        respData?.Message ||
        respData?.error?.message ||
        respData?.error_description ||
        respData?.message ||
        (respData ? JSON.stringify(respData) : null) ||
        err.message ||
        'An unexpected error occurred';
      logToDatadog('store_creation_failed', { error: sanitizeError(err), message, storeName: storeInput.name, partialState }, 'error');
      setError(message);
      setSteps(prev =>
        prev.map(s =>
          s.status === StepStatus.IN_PROGRESS
            ? { ...s, status: StepStatus.FAILED, detail: message }
            : s
        )
      );
      setShowRetryModal(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateStore = () => runCreation();
  const handleRetry = () => runCreation(partialState ?? undefined);

  const getStepIcon = (status: StepStatus) => {
    switch (status) {
      case StepStatus.SUCCESS:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case StepStatus.FAILED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case StepStatus.IN_PROGRESS:
        return <LoadingSpinner size={20} />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="p-6 bg-white border-b border-gray-200">
        <div className="md:flex md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              Flipdish Store Creation
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Create a new Property, clone the golden template sales channel, set up menu and delivery fee — all in one step.
            </p>
          </div>
          <div className="mt-4 flex items-center gap-3 md:ml-4 md:mt-0">
            <button
              type="button"
              role="switch"
              aria-checked={skipMenu}
              onClick={() => setSkipMenu(v => !v)}
              title="Skip menu creation and assignment — store will have no menu attached"
              className="flex items-center gap-2 cursor-pointer select-none focus:outline-none"
            >
              <span className="text-sm font-medium text-gray-600">Skip menu</span>
              <span
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  skipMenu ? 'bg-amber-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    skipMenu ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </span>
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={flipdishOnly}
              onClick={() => setFlipdishOnly(v => !v)}
              title="Skip Manna steps and only run the Flipdish portion of the flow"
              className="flex items-center gap-2 cursor-pointer select-none focus:outline-none"
            >
              <span className="text-sm font-medium text-gray-600">Flipdish only</span>
              <span
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                  flipdishOnly ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    flipdishOnly ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </span>
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Clear
            </button>
            {hasFailedPartial ? (
              <button
                type="button"
                disabled={isCreating}
                onClick={handleRetry}
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm ${
                  !isCreating
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-amber-300 cursor-not-allowed'
                }`}
              >
                Retry from failed step
                {isCreating && (
                  <span className="ml-2">
                    <LoadingSpinner size={18} />
                  </span>
                )}
              </button>
            ) : (
              <button
                type="button"
                disabled={!isFormValid || isCreating}
                onClick={() => flipdishOnly ? setShowFlipdishOnlyModal(true) : handleCreateStore()}
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm ${
                  isFormValid && !isCreating
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-300 cursor-not-allowed'
                }`}
              >
                Create Store
                {isCreating && (
                  <span className="ml-2">
                    <LoadingSpinner size={18} />
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      {isCreating && (
        <div className="mx-6 mt-4 p-4" style={{ backgroundColor: '#FFF3CD', boxShadow: 'none', borderLeft: '4px solid #F59E0B', borderTop: 'none', borderRight: 'none', borderBottom: 'none' }}>
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: '#D97706' }} />
            <div>
              <h3 className="text-sm font-bold text-black">Store creation in progress — please do not close or refresh this page</h3>
              <p className="text-sm mt-1 text-black">
                This process involves multiple steps with Flipdish and can take several minutes to complete.
                You can switch to other tabs in your browser while waiting — just don't close this one or change pages on the portal.
                Some steps may retry automatically if Flipdish needs time to process — this is normal behaviour.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* API Configuration (collapsed by default) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => setShowConfig(!showConfig)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center">
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    API Configuration
                  </span>
                  {isConfigValid && (
                    <CheckCircleIcon className="ml-2 h-5 w-5 text-green-500" />
                  )}
                </div>
                <svg
                  className={`h-5 w-5 text-gray-400 transition-transform ${showConfig ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
              {showConfig && (
                <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'App / Brand ID', value: config.appId },
                    { label: 'Org ID', value: config.orgId },
                    { label: 'Template Sales Channel', value: config.templateSalesChannelId },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      <input
                        type="text"
                        value={value}
                        readOnly
                        disabled
                        className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-500 bg-gray-100 shadow-sm ring-1 ring-inset ring-gray-200 sm:text-sm cursor-not-allowed"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Store Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                New Store Details
              </h3>

              {/* — Store Info — */}
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Store Info
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Store Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={storeInput.name}
                    onChange={e => updateStoreInput('name', e.target.value)}
                    placeholder="e.g. Bobs Burgers"
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Used as Property name, Sales Channel name, and menu name
                  </p>
                </div>
                <div className={flipdishOnly ? 'opacity-40 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category {!flipdishOnly && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={mannaInput.category}
                    onChange={e => updateMannaInput('category', e.target.value)}
                    disabled={flipdishOnly}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select category...</option>
                    {merchantCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.displayText}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={storeInput.phone}
                    onChange={e => updateStoreInput('phone', e.target.value)}
                    placeholder="e.g. +353 1 234 5678"
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={storeInput.email}
                    onChange={e => updateStoreInput('email', e.target.value)}
                    placeholder="e.g. store@manna.aero"
                    className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm ${
                      storeInput.email.trim().length > 0 && !isValidEmail
                        ? 'ring-red-300 focus:ring-red-500'
                        : 'ring-gray-300 focus:ring-blue-600'
                    }`}
                  />
                  {storeInput.email.trim().length > 0 && !isValidEmail && (
                    <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select
                    value={storeInput.currency}
                    onChange={e => setStoreInput(prev => ({ ...prev, currency: e.target.value as Currency }))}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
                <div className={flipdishOnly ? 'opacity-40 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cell {!flipdishOnly && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={mannaInput.cellId}
                    onChange={e => updateMannaInput('cellId', e.target.value)}
                    disabled={flipdishOnly}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select cell...</option>
                    {cells.map(cell => (
                      <option key={cell.id} value={cell.id}>{cell.cellName}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* — Store Address — */}
              <div className="border-t border-gray-200 mt-6 pt-5">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Store Address
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {cc === 'IE' || cc === 'GB' ? 'Line 1 (House / Building)' : 'Address Line 1'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={storeInput.address.addressLine1}
                    onChange={e => updateAddress('addressLine1', e.target.value)}
                    placeholder={cc === 'US' ? 'e.g. 123 Main St' : 'e.g. Unit 629'}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {cc === 'IE' ? 'Line 2 (Street)' : cc === 'GB' ? 'Line 2 (Street)' : 'Address Line 2'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={storeInput.address.addressLine2}
                    onChange={e => updateAddress('addressLine2', e.target.value)}
                    placeholder={cc === 'IE' ? 'e.g. Blanchardstown Road' : cc === 'GB' ? 'e.g. High Street' : 'e.g. Apt 4B'}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                {cc === 'GB' && (
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Line 3 (Area / Town) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={storeInput.address.addressLine3 ?? ''}
                      onChange={e => updateAddress('addressLine3', e.target.value)}
                      placeholder="e.g. Shoreditch"
                      className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {cc === 'IE' ? 'Line 3 (City / Town)' : 'City / Town'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={storeInput.address.city}
                    onChange={e => updateAddress('city', e.target.value)}
                    placeholder={cc === 'IE' ? 'e.g. Dublin 15' : cc === 'GB' ? 'e.g. London' : 'e.g. San Francisco'}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                {cc === 'US' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={2}
                      value={storeInput.address.state ?? ''}
                      onChange={e => updateAddress('state', e.target.value.toUpperCase())}
                      placeholder="e.g. CA"
                      className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">Two-letter USPS abbreviation</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {cc === 'IE' ? 'Eircode' : cc === 'US' ? 'Zip Code' : 'Postcode'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={storeInput.address.postcode}
                    onChange={e => updateAddress('postcode', e.target.value)}
                    placeholder={cc === 'IE' ? 'e.g. D15 CTK4' : cc === 'US' ? 'e.g. 94105' : 'e.g. EC1A 1BB'}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={storeInput.address.countryCode}
                    onChange={e => updateAddress('countryCode', e.target.value)}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  >
                    <option value="IE">Ireland</option>
                    <option value="GB">United Kingdom</option>
                    <option value="US">United States</option>
                  </select>
                </div>
              </div>
              </div>

              {/* — Coordinates — */}
              <div className="border-t border-gray-200 mt-6 pt-5">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Coordinates <span className="text-xs font-normal normal-case text-gray-400">(optional)</span>
              </h4>
              <p className="text-xs text-gray-400 mb-3">
                Defaults to Dublin city center if not provided. Right-click the location in Google Maps to copy coordinates.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude
                  </label>
                  <input
                    type="text"
                    value={storeInput.latitude}
                    onChange={e => updateStoreInput('latitude', e.target.value)}
                    placeholder="e.g. 53.41291"
                    className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm ${
                      !isLatValid ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-blue-600'
                    }`}
                  />
                  {!isLatValid && <p className="text-xs text-red-500 mt-1">Must be a valid number (e.g. 53.41291)</p>}
                  {hasPartialCoords && lngRaw !== '' && latRaw === '' && <p className="text-xs text-red-500 mt-1">Required when longitude is set</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude
                  </label>
                  <input
                    type="text"
                    value={storeInput.longitude}
                    onChange={e => updateStoreInput('longitude', e.target.value)}
                    placeholder="e.g. -8.24389"
                    className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm ${
                      !isLngValid ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-blue-600'
                    }`}
                  />
                  {!isLngValid && <p className="text-xs text-red-500 mt-1">Must be a valid number (e.g. -8.24389)</p>}
                  {hasPartialCoords && latRaw !== '' && lngRaw === '' && <p className="text-xs text-red-500 mt-1">Required when latitude is set</p>}
                </div>
              </div>
              </div>

              {/* — Operations — */}
              <div className={`border-t border-gray-200 mt-6 pt-5 ${flipdishOnly ? 'opacity-40 pointer-events-none' : ''}`}>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Operations {flipdishOnly && <span className="text-xs font-normal text-gray-400 normal-case tracking-normal">(Manna only — skipped)</span>}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={mannaInput.position}
                    onChange={e => updateMannaInput('position', e.target.value)}
                    placeholder="e.g. 1"
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Time (mins) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={mannaInput.leadTime}
                    onChange={e => updateMannaInput('leadTime', e.target.value)}
                    placeholder="e.g. 35"
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Travel Time (mins) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={mannaInput.travelTime}
                    onChange={e => updateMannaInput('travelTime', e.target.value)}
                    placeholder="e.g. 5"
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Throttling (per hour)
                  </label>
                  <input
                    type="number"
                    value={mannaInput.orderHrLimit}
                    onChange={e => updateMannaInput('orderHrLimit', e.target.value)}
                    placeholder="0 = no limit"
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Landing Zone <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={mannaInput.landingZoneId}
                    onChange={e => updateMannaInput('landingZoneId', e.target.value)}
                    disabled={!mannaInput.cellId}
                    className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">{mannaInput.cellId ? 'Select landing zone...' : 'Select a cell first'}</option>
                    {landingZones.map(lz => (
                      <option key={lz.id} value={lz.id}>{lz.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              </div>

              {/* — Store Logos — */}
              <div className={`border-t border-gray-200 mt-6 pt-5 ${flipdishOnly ? 'opacity-40 pointer-events-none' : ''}`}>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Store Logos {flipdishOnly && <span className="text-xs font-normal text-gray-400 normal-case tracking-normal">(Manna only — skipped)</span>}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo <span className="text-red-500">*</span>
                  </label>
                  <input
                    key={fileInputKey}
                    type="file"
                    accept="image/*"
                    onChange={e => setLogoFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {logoFile && <p className="text-xs text-green-600 mt-1">{logoFile.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Large Logo <span className="text-red-500">*</span>
                  </label>
                  <input
                    key={fileInputKey + 1}
                    type="file"
                    accept="image/*"
                    onChange={e => setLargeLogoFile(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {largeLogoFile && <p className="text-xs text-green-600 mt-1">{largeLogoFile.name}</p>}
                </div>
              </div>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <h3 className="text-sm font-semibold text-blue-800 uppercase tracking-wide mb-2">
                Settings cloned from golden template
              </h3>
              <p className="text-xs text-blue-700">
                All operational settings (delivery, collection, order config, service charge, opening hours) are inherited
                from the golden template sales channel (<span className="font-mono">{config.templateSalesChannelId}</span>).
                To change defaults for future stores, update the template in the Flipdish portal.
              </p>
            </div>

            {/* Debug panel — testing only */}
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50">
              <button
                type="button"
                onClick={() => setShowDebugPanel(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-left"
              >
                <span className="text-xs font-mono text-gray-400">🛠 Debug / Testing</span>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform ${showDebugPanel ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
              {showDebugPanel && (
                <div className="px-4 pb-4 space-y-3">
                  <p className="text-xs text-gray-500">
                    Force a failure at a specific step to test the retry flow. Uncheck before pressing Retry to let it proceed normally.
                  </p>
                  <div className="columns-2 gap-2 space-y-2">
                    {[
                      { step: 0,    label: 'Step 0 — Auth' },
                      { step: 1,    label: 'Step 1 — Create Property' },
                      { step: 2,    label: 'Step 2 — Opening hours' },
                      { step: 3,    label: 'Step 3a — Clone SC' },
                      { step: '3b', label: 'Step 3b — Rename SC' },
                      { step: 4,    label: 'Step 4 — Publish SC' },
                      { step: 5,    label: 'Step 5 — Wait for store' },
                      { step: 6,    label: 'Step 6a — Create Store Group' },
                      { step: '6b', label: 'Step 6b — Assign store to group' },
                      { step: 7,    label: 'Step 7a — Create menu' },
                      { step: '7b', label: 'Step 7b — Set delivery fee' },
                      { step: 8,     label: 'Step 8 — Assign menu' },
                      { step: 9,     label: 'Step 9 — Update address' },
                      { step: 10,    label: 'Step 10 — Verify store' },
                      { step: 'm11', label: 'Step M11 — Create Partner' },
                      { step: 'm12', label: 'Step M12 — Upload logos' },
                      { step: 'm13', label: 'Step M13 — Create Manna POS' },
                    ].map(({ step, label }) => (
                      <label key={String(step)} className="flex items-center gap-2 cursor-pointer select-none break-inside-avoid">
                        <input
                          type="checkbox"
                          checked={debugFailAtStep === step}
                          onChange={e => setDebugFailAtStep(e.target.checked ? step : undefined)}
                          className="rounded border-gray-300 text-red-500 focus:ring-red-400"
                        />
                        <span className={`text-xs font-mono ${String(step).includes('b') ? 'text-orange-600' : String(step).includes('m') ? 'text-purple-600' : 'text-gray-600'}`}>
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 italic">Steps labelled <span className="text-orange-600 not-italic font-mono">b</span> are sub-steps within a Flipdish step. Steps labelled <span className="text-purple-600 not-italic font-mono">M</span> are Manna steps (only fire when Flipdish only is off).</p>
                  {debugFailAtStep !== undefined && (
                    <p className="text-xs text-red-600 font-medium">
                      ⚠ Will fail at step {String(debugFailAtStep)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                Progress
              </h3>
              {[
                { title: 'Flipdish', stepsSlice: steps.slice(0, FD_STEPS.length), offset: 0, skipped: false },
                { title: 'Manna', stepsSlice: steps.slice(FD_STEPS.length), offset: FD_STEPS.length, skipped: flipdishOnly },
              ].map(({ title, stepsSlice, skipped }) => (
                <div key={title} className={`mb-4 ${skipped ? 'opacity-40' : ''}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{title}</p>
                    {skipped && <span className="text-xs text-gray-400 italic">(skipped)</span>}
                  </div>
                  <div className="space-y-3">
                    {stepsSlice.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getStepIcon(step.status)}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${
                            step.status === StepStatus.SUCCESS
                              ? 'text-green-700'
                              : step.status === StepStatus.FAILED
                              ? 'text-red-700'
                              : step.status === StepStatus.IN_PROGRESS
                              ? 'text-blue-700'
                              : 'text-gray-500'
                          }`}>
                            {step.label}
                          </p>
                          {step.detail && (
                            <p className="text-xs text-gray-400 mt-0.5 break-words">
                              {step.detail}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {error && !showRetryModal && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Creation failed</h3>
                    <p className="text-sm text-red-700 mt-1 break-words">{error}</p>
                    {partialState && (
                      <button
                        type="button"
                        onClick={() => setShowRetryModal(true)}
                        className="mt-3 rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-800 hover:bg-red-200"
                      >
                        Retry from failed step
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {result && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-green-800">
                      Store created successfully
                    </h3>
                    <dl className="mt-3 space-y-2 text-sm">
                      {[
                        { label: 'Store ID', value: result.storeId },
                        { label: 'Sales Channel ID', value: result.salesChannelId },
                        { label: 'Property ID', value: result.propertyId },
                        { label: 'Sales Channel Group ID', value: result.storeGroupId },
                        { label: 'Menu ID', value: result.menuId },
                        { label: 'Partner', value: result.partnerName },
                      ].filter(({ value }) => value != null).map(({ label, value }) => (
                        <div key={label}>
                          <dt className="text-green-600 text-xs font-medium">{label}</dt>
                          <dd className="text-green-900 font-mono">{value}</dd>
                        </div>
                      ))}
                      <div>
                        <dt className="text-green-600 text-xs font-medium">Portal</dt>
                        <dd>
                          <a
                            href={result.portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-800 underline hover:text-green-900 font-mono text-xs break-all"
                          >
                            {result.portalUrl}
                          </a>
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-3 text-xs text-green-600">
                      Sales channel was published automatically as part of the flow.
                    </p>
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="rounded-md bg-green-100 px-3 py-1.5 text-sm font-medium text-green-800 hover:bg-green-200"
                      >
                        Create another
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-100 rounded-lg p-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                What happens when you click Create Store
              </h3>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Flipdish</p>
                <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside mb-3">
                <li>Access token obtained via backend</li>
                <li>Property created via v3.0 API</li>
                <li>Property opening hours set to all day</li>
                <li>Golden template sales channel cloned (inherits all settings)</li>
                <li>Sales channel published</li>
                <li>Wait for Flipdish to provision store (up to 10 min)</li>
                <li>Sales Channel Group created &amp; store assigned</li>
                <li>Menu created with DELIVERY_FEE (4.99)</li>
                <li>Menu assigned to store</li>
                <li>Store address updated</li>
                <li>Store verified under <span className="font-mono">{config.appId}</span></li>
              </ol>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Manna</p>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside" start={11}>
                <li>Partner created in Manna system</li>
                <li>Store logos uploaded</li>
                <li>Manna store (Point of Sale) created with default opening hours</li>
              </ol>
              <p className="text-xs text-gray-400 mt-2">
                The sales channel is published automatically as part of the flow.
              </p>
            </div>
          </div>
        </div>
      {/* Retry modal */}
      {showRetryModal && partialState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowRetryModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 mt-0.5 text-red-500" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Store creation failed</h3>
                <p className="text-sm text-gray-600 mt-2">
                  The process stopped at step <span className="font-semibold">{(partialState.failedAtStep ?? 0) + 1}</span>
                  {' '}of {flipdishOnly ? FD_STEPS.length : ALL_STEPS.length}:
                  <span className="font-semibold ml-1">
                    {(() => {
                      const idx = partialState.failedAtStep ?? 0;
                      return idx < ALL_STEPS.length ? ALL_STEPS[idx].label : `Step ${idx + 1}`;
                    })()}
                  </span>
                </p>
                {error && (
                  <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-700 break-words">{error}</p>
                  </div>
                )}
                <p className="text-sm text-gray-600 mt-3">
                  All previously completed steps have been saved. You can retry from where it failed
                  without re-creating anything that already succeeded.
                </p>
                {(partialState.failedAtStep ?? 0) <= 5 && (
                  <p className="text-sm text-amber-700 mt-2 bg-amber-50 rounded-md p-2 border border-amber-200">
                    This step involves Flipdish APIs which can be slow. Consider waiting <span className="font-semibold">30–60 seconds</span> before retrying to give Flipdish time to process.
                  </p>
                )}
                {(partialState.propertyId || partialState.partnerId) && (
                  <div className="mt-3 text-xs text-gray-500 space-y-0.5">
                    <p>Progress so far:</p>
                    {partialState.propertyId && <p className="font-mono">Property: {partialState.propertyId}</p>}
                    {partialState.salesChannelId && <p className="font-mono">Sales Channel: {partialState.salesChannelId}</p>}
                    {partialState.storeId ? <p className="font-mono">Store ID: {partialState.storeId}</p> : null}
                    {partialState.storeGroupId ? <p className="font-mono">Store Group: {partialState.storeGroupId}</p> : null}
                    {partialState.menuId ? <p className="font-mono">Menu: {partialState.menuId}</p> : null}
                    {partialState.partnerId ? <p className="font-mono">Partner ID: {partialState.partnerId}</p> : null}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowRetryModal(false)}
                className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                Retry from failed step
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Flipdish-only confirmation modal */}
      {showFlipdishOnlyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFlipdishOnlyModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 mt-0.5 text-amber-500" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Flipdish only — Manna steps will be skipped</h3>
                <p className="text-sm text-gray-600 mt-2">
                  You have <span className="font-semibold">Flipdish only</span> mode enabled. This will run all 11 Flipdish steps
                  (Property, Sales Channel, menu, address etc.) but will <span className="font-semibold">not</span> create a Partner,
                  upload logos, or create a Manna store.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  The Flipdish store will exist but will <span className="font-semibold">not be visible or active in the Manna system</span> until
                  the Manna steps are completed separately.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowFlipdishOnlyModal(false)}
                className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setShowFlipdishOnlyModal(false); handleCreateStore(); }}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                Yes, run Flipdish steps only
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default StoreCreation;
